import ModulePageLayout from "./ModulePageLayout";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileLock,
  Users,
  History,
  MapPin,
  ScrollText,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: MapPin,
    title: "EU hosting by default",
    body: "Customer data for Zynthoro is hosted in the European Union, inside the same AI-native ERP that runs your business operations.",
  },
  {
    icon: Users,
    title: "Role-based access control",
    body: "Granular roles and access levels (L1–L10) across every module, so finance, HR and production data each stay with the right people.",
  },
  {
    icon: History,
    title: "Audit trail built in",
    body: "Audit trails are part of the platform, not an add-on. Changes to records, approvals and configuration are logged automatically.",
  },
  {
    icon: FileLock,
    title: "GDPR-aligned data handling",
    body: "Data processing, retention controls and standard GDPR agreements (DPA, SLA, cookie, privacy and terms policies) are published and maintained.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance across modules",
    body: "Compliance isn't a silo — the same controls apply to invoices, HR records, production lots and operations workflows inside one ledger.",
  },
  {
    icon: ScrollText,
    title: "Clear legal surface",
    body: "Current versions of the DPA, SLA, Terms, Privacy and Cookie policies are available at /legal and version-controlled.",
  },
];

export default function ComplianceModule() {
  return (
    <ModulePageLayout
      title="Compliance module — Zynthoro AI ERP for European SMEs"
      description="Zynthoro's compliance module provides EU hosting, GDPR-aligned data handling, audit trails and role-based access inside one AI-native ERP for European SMEs."
      breadcrumbLabel="Compliance"
      heroEyebrow="Modules · Compliance & Security"
      heroHeading="Compliance that lives inside your ERP."
      heroBody="Zynthoro's compliance capability is woven into the same AI-native ERP that runs your finance, HR, operations and production. EU hosting, GDPR-aligned data handling, audit trails and role-based access are part of the platform, not an add-on."
    >
      {/* Capabilities */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl mb-10 zy-reveal">
            <h2 className="zy-h2">What the compliance module covers</h2>
            <p className="zy-body mt-4">
              The controls listed below apply across every Zynthoro module, so finance, HR,
              production and operations each inherit the same compliance posture.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="zy-reveal p-6 rounded-2xl border bg-white"
                  style={{ borderColor: "#eee" }}
                  data-testid={`compliance-cap-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4"
                    style={{ background: "#EAF0FF" }}
                  >
                    <Icon size={20} style={{ color: "#1A4FFF" }} />
                  </span>
                  <h3 className="text-[16px] font-bold tracking-tight mb-1.5">{c.title}</h3>
                  <p className="text-[13.5px] text-[#555] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who it is for */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Who Zynthoro's compliance capability is designed for</h2>
            <p className="zy-body mt-4">
              Zynthoro is used by European small and medium businesses that need defensible
              controls — GDPR-ready data handling, audit trails and role-based access — without
              buying and stitching together separate compliance point tools.
            </p>
            <p className="zy-body mt-4">
              Teams in regulated spaces (food, cosmetics, pharma, light manufacturing) run
              traceability and quality control inside the same ledger, so the compliance
              evidence they need already exists in production data.
            </p>
          </div>
        </div>
      </section>

      {/* How it fits */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">How compliance fits in the Zynthoro ERP ecosystem</h2>
            <p className="zy-body mt-4">
              Compliance isn't a separate module hiding behind the ERP — it is the shared
              scaffolding under every module. Audit trail entries flow from finance postings,
              inventory movements and HR changes alike. Role-based access controls what each
              teammate can see across the whole platform.
            </p>
            <p className="zy-body mt-4">
              The current Data Processing Agreement, Service Level Agreement, Terms of Service,
              Privacy Policy and Cookie Policy are published and version-controlled.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Compliance — common questions</h2>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Is Zynthoro a dedicated GDPR tool?</h3>
              <p className="zy-body mt-2">
                No. Zynthoro is an AI-native ERP for SMEs. Compliance is one of its modules, and
                its controls apply across the rest of the platform rather than existing as a
                separate point tool.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Where is my data hosted?</h3>
              <p className="zy-body mt-2">
                Customer data for Zynthoro is hosted in the European Union.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Which legal documents does Zynthoro publish?</h3>
              <p className="zy-body mt-2">
                A Data Processing Agreement, Service Level Agreement, Terms of Service, Privacy
                Policy and Cookie Policy are available at /legal and version-controlled.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">How do audit trails work?</h3>
              <p className="zy-body mt-2">
                Audit trails are built into the platform. Changes to records, approvals and
                configuration across modules are logged automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related reading */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Related reading</h2>
            <ul className="mt-5 space-y-2.5 zy-body">
              <li><Link to="/blog/gdpr-compliance-software" className="zy-link">GDPR Compliance Software for SMEs: An Honest Guide</Link></li>
              <li><Link to="/legal/dpa" className="zy-link">Data Processing Agreement</Link> · <Link to="/legal/privacy-policy" className="zy-link">Privacy Policy</Link> · <Link to="/legal/sla" className="zy-link">Service Level Agreement</Link></li>
              <li><Link to="/modules/supply-chain" className="zy-link">Supply chain module</Link> · <Link to="/modules/manufacturing" className="zy-link">Manufacturing module</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </ModulePageLayout>
  );
}
